import "./LabFiltersComponent.scss";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import CommonService from "../../../shared/services/common.service";
import {Field, FieldProps, Form, Formik, FormikProps, FormikValues} from "formik";
import FormikAutoCompleteDropdown
    from "../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import FormikDatePickerComponent from "../../../shared/components/core/formik-date-picker/FormikDatePickerComponent";
import {APIConfig} from "../../../constants";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import formValidationRules from "../../../shared/services/validation.service";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import * as Yup from "yup";

interface LabFiltersComponentProps {
    filtersData: any;
    onFiltersApply: (filterData: any) => void;
    setFilterData?: any;
    FiltersInitialState?: any;
}

const validationSchema = Yup.object().shape({
    startDate: Yup.mixed().nullable().when("endDate", {
        is: (endDate: Date) => {
            return Boolean(endDate)
        },
        then: Yup.mixed().nullable().required("Start date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    }),
    endDate: Yup.mixed().nullable().when("startDate", {
        is: (startDate: Date) => {
            return Boolean(startDate)
        },
        then: Yup.mixed().nullable().required("End date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    })
}, [['startDate', 'endDate']]);

const LabFiltersComponent = (props: LabFiltersComponentProps) => {

    const {filtersData, onFiltersApply, setFilterData, FiltersInitialState} = props;
    const [dataFiltersInitialState, setDataFiltersInitialState] = useState(filtersData);
    const {userList, labStatusList} = useSelector((state: IRootReducerState) => state.staticData);
    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const onFiltersApplyHandler = useCallback((values: any) => {
        const payload = {
            ...values
        }
        payload.labTypes = payload?.labTypesObjs?.map((item: any) => item?.code);
        payload.statuses = payload?.statusesObjs?.map((item: any) => item?.code);
        payload.cities = payload.citiesObjs.map((item: any) => item.city);
        payload.createdByIds = payload.createdByObjs.map((item: any) => item.id);
        payload.startDate = payload.startDate ? CommonService.convertDateFormat(values['startDate'], 'YYYY-MM-DD') : "";
        payload.endDate = payload.endDate ? CommonService.convertDateFormat(values['endDate'], 'YYYY-MM-DD') : "";
        onFiltersApply(payload);

    }, [onFiltersApply]);

    useEffect(() => {
        setDataFiltersInitialState(filtersData);
    }, [filtersData]);

    return (
        <div className={'lab-filters-component screen t-form-screen'}>
            <>
                <div className="drawer-sub-header horizontal-vertical-center-align">
                    <HeadingComponent title={"Filters"}/>
                    <div className={'filters-clear-all-button'}
                         onClick={() => {
                             formikRef.current && formikRef.current.resetForm()
                             setFilterData(FiltersInitialState)
                         }}
                    >
                        Clear all
                    </div>
                </div>
                <div className="t-form t-form-drawer">
                    <Formik
                        initialValues={dataFiltersInitialState}
                        enableReinitialize={true}
                        onSubmit={onFiltersApplyHandler}
                        innerRef={formikRef}
                        validationSchema={validationSchema}
                    >
                        {({isSubmitting, values, isValid, setFieldValue, validateForm, errors, dirty}) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [values, validateForm]);
                            return (
                                <Form noValidate={true} className={"location-filters-holder"}>
                                    <div className="t-form-controls-wrapper">
                                        <Field name={'labTypesObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        label={'Lab Type'}
                                                        placeholder={'Select Lab Type'}
                                                        valueKey={'code'}
                                                        multiple={true}
                                                        data={CommonService._staticData.labType}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.title) || '')}

                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'createdByObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Added by'}
                                                        multiple={true}
                                                        data={userList}
                                                        valueKey={"id"}
                                                        placeholder={'Select Added by'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.firstName + ' ' + option.lastName) || '')}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'citiesObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        label={'City'}
                                                        placeholder={"start type for city"}
                                                        formikField={field}
                                                        url={APIConfig.CITY_LIST.URL}
                                                        method={APIConfig.CITY_LIST.METHOD}
                                                        searchMode={'serverSide'}
                                                        openOnFocus={true}
                                                        dataListKey={"data.docs"}
                                                        data={[]}
                                                        multiple={true}
                                                        valueKey={'city'}
                                                        getOptionLabel={(option => (option.city) || '')}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'statusesObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Lab Status'}
                                                        multiple={true}
                                                        data={labStatusList}
                                                        valueKey={"code"}
                                                        placeholder={'Select Lab Status'}
                                                        formikField={field}
                                                        getOptionLabel={(option) => (option.title)}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'startDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'Start Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Field name={'endDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'End Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className="drawer-actions form-action-section">
                                        <ButtonComponent variant={"contained"}
                                                         fullWidth={true}
                                                         disabled={!(isValid) || !(dirty)}
                                                         type={"submit"}
                                        >
                                            Apply
                                        </ButtonComponent>
                                    </div>
                                </Form>
                            )
                        }
                        }
                    </Formik>
                </div>
            </>

        </div>
    );

};

export default LabFiltersComponent;
